/**
 * BubbleAbacus — a tactile "bubble abacus" that replaces the flat benefit/stat
 * tiles on the Bubblé product page. Each benefit is a rod with a glossy bubble
 * bead you slide across (drag, click, or arrow keys) to reveal the fact; the
 * stats become tally rods whose numbers count up and whose beads click into
 * place when the board scrolls into view.
 *
 *   <BubbleAbacus benefits={[{icon,title,summary,more}]} stats={[{num,lbl}]} />
 *
 * ZoneAbacus (named export) turns the multi-use toggle into a single rod of
 * zone beads — a controlled selector driven by `active` / `onChange`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './BubbleAbacus.module.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// Pointer capture is a nice-to-have; it throws on synthetic events with no
// active pointer, so swallow that without letting the handler bail.
const capture = (e, on) => {
  const fn = on ? 'setPointerCapture' : 'releasePointerCapture';
  try {
    e.currentTarget[fn]?.(e.pointerId);
  } catch (err) {
    void err;
  }
};

/* ------------------------------------------------------------------ */
/* One benefit rod: a bubble bead that slides along a wire to reveal.  */
/* ------------------------------------------------------------------ */
function BenefitRod({ item }) {
  const railRef = useRef(null);
  const revealRef = useRef(null);
  const drag = useRef(null); // { startX, startPos, travel, moved }
  const posRef = useRef(0); // live drag position, dodges stale-closure reads
  const suppressClick = useRef(false); // swallow the click that ends a real drag

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(0); // 0..1, live while dragging
  const [dragging, setDragging] = useState(false);

  // p drives the bead position + progressive reveal.
  const p = dragging ? pos : open ? 1 : 0;

  const travel = () => {
    const rail = railRef.current;
    if (!rail) return 0;
    return Math.max(0, rail.clientWidth - 44 - 12); // bead width + side insets
  };

  // Pointer events power the rich *drag* gesture; the plain onClick below owns
  // the open/close toggle (so mouse, touch-tap, keyboard and synthetic clicks
  // all work even on platforms that don't synthesize pointer events).
  const onPointerDown = (e) => {
    drag.current = { startX: e.clientX, startPos: open ? 1 : 0, travel: travel(), moved: 0 };
    posRef.current = open ? 1 : 0;
    setPos(posRef.current);
    capture(e, true);
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    d.moved = Math.max(d.moved, Math.abs(dx));
    if (d.moved > 4 && !dragging) setDragging(true);
    posRef.current = d.travel > 0 ? clamp(d.startPos + dx / d.travel, 0, 1) : d.startPos;
    setPos(posRef.current);
  };

  const endDrag = (e) => {
    const d = drag.current;
    if (!d) return;
    capture(e, false);
    drag.current = null;
    setDragging(false);
    if (d.moved > 6) {
      suppressClick.current = true; // this was a drag, not a tap
      setOpen(posRef.current > 0.5);
    }
  };

  const onClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    setOpen((o) => !o);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setOpen(false);
    }
  };

  // Keep the reveal height animated to its natural content height.
  const revealOpen = dragging ? pos > 0.04 : open;
  const innerH = revealRef.current ? revealRef.current.scrollHeight : 0;

  return (
    <div className={styles.rod}>
      <div className={styles.rodHead}>
        {item.icon && <span className={styles.rodIcon}>{item.icon}</span>}
        <span className={styles.rodTitle}>{item.title}</span>
        {item.summary && <span className={styles.rodSummary}>{item.summary}</span>}
      </div>

      <div ref={railRef} className={`${styles.rail} ${open ? styles.open : ''}`}>
        <div className={styles.wire} />
        <div className={styles.socket} />
        <button
          type="button"
          className={`${styles.bead} ${dragging ? '' : styles.glide}`}
          style={{ transform: `translateX(${p * travel()}px)` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClick={onClick}
          onKeyDown={onKeyDown}
          aria-expanded={open}
          aria-label={`${item.title} — slide to ${open ? 'hide' : 'reveal'} details`}
        >
          <span className={styles.beadArrow}>{open ? '−' : '→'}</span>
        </button>
      </div>

      <div
        className={styles.reveal}
        style={{
          maxHeight: revealOpen ? innerH || 240 : 0,
          opacity: dragging ? pos : open ? 1 : 0,
          transition: dragging
            ? 'none'
            : 'max-height 0.4s ease, opacity 0.35s ease',
        }}
      >
        <div ref={revealRef} className={styles.revealInner}>
          {item.more}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stat tally rod: count-up number + beads that click into place.     */
/* ------------------------------------------------------------------ */
function StatCounter({ stat, fire, delay }) {
  const m = /^(\D*)(\d+)(.*)$/.exec(stat.num) || [null, '', stat.num, ''];
  const prefix = m[1];
  const target = parseInt(m[2], 10) || 0;
  const suffix = m[3];

  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!fire) return undefined;
    if (prefersReducedMotion() || target === 0) {
      setVal(target);
      return undefined;
    }
    let raf;
    let start;
    const dur = 1100;
    const step = (t) => {
      if (start === undefined) start = t;
      const k = clamp((t - start) / dur, 0, 1);
      const eased = 1 - Math.pow(1 - k, 3);
      setVal(Math.round(target * eased));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [fire, target]);

  const BEADS = 5;
  return (
    <div className={styles.counter}>
      <div className={styles.counterNum}>
        {prefix}
        {val}
        {suffix}
      </div>
      <div className={styles.counterLbl}>{stat.lbl}</div>
      <div className={styles.tally} aria-hidden="true">
        <div className={styles.wire} />
        {Array.from({ length: BEADS }, (_, i) => (
          <span
            key={i}
            className={`${styles.tbead} ${fire ? styles.on : ''}`}
            style={{
              left: `${fire ? 60 + i * 9 : 8 + i * 6}%`,
              transitionDelay: `${delay + i * 70}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The board: stats on top, benefit rods below, in one candy frame.   */
/* ------------------------------------------------------------------ */
export default function BubbleAbacus({ benefits = [], stats = [] }) {
  const boardRef = useRef(null);
  const [fire, setFire] = useState(false);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setFire(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setFire(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={boardRef} className={styles.board}>
      {stats.length > 0 && (
        <div className={styles.counters}>
          {stats.map((s, i) => (
            <StatCounter key={s.num + s.lbl} stat={s} fire={fire} delay={i * 120} />
          ))}
        </div>
      )}

      <div className={styles.rods}>
        {benefits.map((b, i) => (
          <BenefitRod key={b.title || i} item={b} />
        ))}
      </div>

      <span className={styles.hint}>Slide a bead to reveal each fact ✦</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ZoneAbacus: the multi-use selector as one rod of zone beads.       */
/* Controlled — parent owns `active` and updates via `onChange`.      */
/* ------------------------------------------------------------------ */
export function ZoneAbacus({ uses = [], active = 0, onChange }) {
  const set = useCallback((i) => onChange && onChange(i), [onChange]);
  const u = uses[active];
  if (!u) return null;

  return (
    <div className={styles.zoneWrap}>
      <div className={styles.zoneRail}>
        <div className={styles.wire} />
        <div className={styles.zoneBeads} role="tablist" aria-label="Where to use it">
          {uses.map((z, i) => (
            <button
              key={z.k}
              type="button"
              role="tab"
              aria-selected={i === active}
              className={`${styles.zoneBead} ${i === active ? styles.active : ''}`}
              onClick={() => set(i)}
            >
              <span className={styles.zoneDot}>{z.e}</span>
              <span className={styles.zoneName}>{z.k}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.zonePanel}>
        <div className={styles.zoneEmoji}>{u.e}</div>
        <h3>{u.t}</h3>
        <p>{u.p}</p>
      </div>
    </div>
  );
}
